import React, { useEffect, useId, useRef, useState } from 'react';
import { displayValue, fieldByKey, FIELDS, STATUSES } from '../shared/exchanges';
import { StatusBadge } from './components';
import DateInput from '../Composents/DateInput';
import { isBirthDateQuestion } from '../shared/dateValue';
import { normalizeExchangeValue, exchangeFieldError } from '../shared/exchangeValidation';

const EDITABLE = ['lastName', 'firstName', 'email', 'status', 'session'];

function questionAt(student, target) {
  const entry = student.dossier?.data?.find(item => String(item.id) === String(target.entryId));
  const critere = entry?.criteres?.find(item => String(item.id) === String(target.critereId));
  const section = critere?.sousCriteres?.find(item => String(item.id) === String(target.sectionId));
  return section?.questions?.find(item => String(item.id) === String(target.questionId));
}
function fieldValue(student, field, target, property) {
  if (!target) return field === 'session' ? student.sessionId : student[field] ?? '';
  const question = questionAt(student, target);
  if (property === 'code_postal') return question?.code_postal || '';
  return question?.type === 'search' ? question.valueName || question.value || '' : question?.value ?? '';
}

export default function InlineStudentCell({ row, field, fields = FIELDS, sessions = [], api, onUpdated, as: Container = 'td', disabled = false, editGuards, target, valueProperty = 'value' }) {
  const definition = fieldByKey(field, fields);
  const question = definition?.question;
  const inputQuestion = valueProperty === 'code_postal' ? { type: 'text', maxLength: 100 } : question;
  const currentValue = fieldValue(row, field, target, valueProperty);
  // Hidden conditional questions are not missing answers. Keep any stored value visible.
  const emptyValue = Container === 'td' ? ''
    : target && questionAt(row, target)?.visible === false ? 'Non applicable' : 'À renseigner';
  const booleanValue = inputQuestion?.type === 'checkbox' || typeof currentValue === 'boolean';
  const editable = (EDITABLE.includes(field) && (field !== 'session' || sessions.length > 0)) || Boolean(question && question.type !== 'file');
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const originalValue = useRef('');
  const instanceId = useId();
  const input = useRef(null);
  const cell = useRef(null);
  const label = valueProperty === 'code_postal' ? 'Code postal' : definition?.label || 'Champ retiré du template';
  const editing = draft !== null;
  useEffect(() => {
    if (editing) { input.current?.focus(); input.current?.select?.(); }
  }, [editing]);

  useEffect(() => {
    if (!editGuards) return undefined;
    const guards = editGuards.current;
    guards.set(instanceId, () => ({ busy: busyRef.current, dirty: draft !== null && draft !== originalValue.current }));
    return () => guards.delete(instanceId);
  }, [editGuards, instanceId, draft]);

  function edit() {
    if (!editable || disabled || busyRef.current || draft !== null) return;
    originalValue.current = String(currentValue);
    setDraft(originalValue.current); setError('');
  }
  function cancel() {
    if (busyRef.current) return;
    setDraft(null); setError(''); cell.current?.focus();
  }
  async function save() {
    if (disabled || busyRef.current || draft === null || !input.current?.reportValidity()) return;
    const value = booleanValue ? (draft === '' ? '' : draft === 'true') : draft.trim();
    const invalid = inputQuestion && exchangeFieldError(inputQuestion, value);
    if (invalid) { setError(invalid); return; }
    if (String(value) === originalValue.current) { cancel(); return; }
    busyRef.current = true; setBusy(true); setError('');
    try {
      // Names are stored as answers in the dossier, using the same API as its full editor.
      const { student } = await api(`/students/${encodeURIComponent(row.id)}`);
      if (String(fieldValue(student, field, target, valueProperty)) !== originalValue.current) {
        onUpdated(student);
        throw new Error('Ce champ a été modifié entre-temps. Appuyez sur Échap puis recommencez avec sa nouvelle valeur.');
      }
      const body = { changes: [], expectedUpdatedAt: student.updatedAt };
      if (target) {
        const currentQuestion = questionAt(student, target);
        if (!currentQuestion) throw new Error('Ce champ est absent du dossier. Fermez puis rouvrez la fiche.');
        body.changes.push({ ...target, value: valueProperty === 'code_postal' ? (currentQuestion.valueName || currentQuestion.value || '') : value,
          ...(valueProperty === 'code_postal' ? { code_postal: value } : {}) });
      } else if (question || field === 'lastName' || field === 'firstName') {
        const questionId = question?.id ?? (field === 'lastName' ? 112 : 113);
        for (const entry of student.dossier.data || []) {
          for (const critere of entry.criteres || []) {
            if (Number(critere.id) === 50) continue;
            for (const section of critere.sousCriteres || []) {
              const question = section.questions?.find(item => String(item.id) === String(questionId));
              if (question) body.changes.push({ entryId: entry.id, critereId: critere.id, sectionId: section.id, questionId: question.id, value });
            }
          }
        }
        if (!body.changes.length) throw new Error('Ce champ est absent du dossier. Ouvrez la fiche pour le vérifier.');
      } else body[field === 'session' ? 'sessionId' : field] = value;
      const result = await api(`/students/${encodeURIComponent(row.id)}`, { method: 'PATCH', body });
      onUpdated(result.student); setDraft(null); cell.current?.focus();
    } catch (e) { setError(e.message); }
    finally { busyRef.current = false; setBusy(false); }
  }
  const inputProps = {
    ref: input, value: draft ?? '', disabled: busy || disabled, required: !question,
    'aria-label': `${label} de ${row.firstName} ${row.lastName}`,
    'aria-invalid': Boolean(error), 'aria-describedby': error ? `cell-error-${instanceId}` : undefined,
    onChange: event => { setDraft(inputQuestion ? normalizeExchangeValue(inputQuestion, event.target.value) : event.target.value); setError(''); },
    onKeyDown: event => {
      event.stopPropagation();
      if (event.nativeEvent.isComposing) return;
      if (event.key === 'Enter') { event.preventDefault(); save(); }
      if (event.key === 'Escape') { event.preventDefault(); cancel(); }
    }
  };
  return <Container ref={cell} className={`${editable ? 'ex-editable-cell' : ''} ${Container === 'td' ? '' : 'ex-inline-field'}`} tabIndex={editable && !disabled ? 0 : undefined} aria-label={editable ? `${label} de ${row.firstName} ${row.lastName}` : undefined} aria-disabled={editable && disabled ? true : undefined}
    title={editable ? 'Double-cliquez pour modifier · Entrée pour valider · Échap pour annuler' : 'Champ en lecture seule'}
    onDoubleClick={edit} onKeyDown={event => {
      if (event.target === event.currentTarget && ['Enter', 'F2'].includes(event.key)) { event.preventDefault(); edit(); }
    }}>
    {draft !== null ? <div className="ex-cell-editor">
      {field === 'session' ? <select {...inputProps}>{sessions.map(session => <option key={session.id} value={session.id}>{session.label}</option>)}</select>
        : field === 'status' ? <select {...inputProps}>{Object.entries(STATUSES).map(([value, name]) => <option key={value} value={value}>{name}</option>)}</select>
        : inputQuestion?.type === 'select' && inputQuestion.Options?.length ? <select {...inputProps}><option value="">Non renseigné</option>{inputQuestion.Options.filter(option => option.value !== '').map((option, index) => <option key={index} value={String(option.value)}>{option.text || option.label || String(option.value)}</option>)}</select>
          : booleanValue ? <select {...inputProps}><option value="">Non renseigné</option><option value="true">Oui</option><option value="false">Non</option></select>
            : inputQuestion?.type === 'textarea' ? <textarea {...inputProps} rows={2} maxLength={Number(inputQuestion.maxLength || inputQuestion.maxlength) || 20000} />
              : inputQuestion?.type === 'date' ? <DateInput birthDate={isBirthDateQuestion(inputQuestion)} {...inputProps} onChange={value => { setDraft(value); setError(''); }} />
              : <input {...inputProps} type={field === 'email' ? 'email' : ['email', 'date', 'number', 'tel'].includes(inputQuestion?.type) ? inputQuestion.type : 'text'} maxLength={inputQuestion ? Number(inputQuestion.maxLength || inputQuestion.maxlength) || 20000 : field === 'email' ? 254 : 150} />}
      {field === 'session' && <div className="ex-actions"><button type="button" className="ex-button ex-button--primary" disabled={busy || disabled} onClick={save}>Enregistrer</button><button type="button" className="ex-button" disabled={busy} onClick={cancel}>Annuler</button></div>}
      <small>{busy ? 'Enregistrement…' : 'Entrée : valider · Échap : annuler'}</small>
      {error && <span id={`cell-error-${instanceId}`} className="ex-cell-error" role="alert">{error}</span>}
    </div> : field === 'status' ? <StatusBadge status={row.status} progress={row.progress} />
      : <span className={field === 'lastName' ? 'ex-last-name' : field === 'session' ? 'ex-session-cell' : ''}>{field === 'session' ? row.session : valueProperty === 'code_postal' ? currentValue || emptyValue : displayValue({ ...row, [field]: currentValue }, field, fields) || emptyValue}</span>}
  </Container>;
}
